import type { Prisma } from "@prisma/client";

export const participantTypes = ["DESIGNER", "BUILDER", "MATERIAL"] as const;
export const designerTypes = ["ARCHITECTE", "BUREAU_ETUDE", "CONTROLEUR_TECHNIQUE", "GEOTECHNICIEN", "MAITRE_OEUVRE"] as const;

export const designerTypeLabels: Record<typeof designerTypes[number], string> = {
  ARCHITECTE: "Architecte",
  BUREAU_ETUDE: "Bureau d'étude",
  CONTROLEUR_TECHNIQUE: "Contrôleur technique",
  GEOTECHNICIEN: "Géotechnicien",
  MAITRE_OEUVRE: "Maître d'œuvre",
} as const;

export const participantSelector = {  
  id: true,
  type: true,
  designerTypes: true,
  materialTypes: true,
  society: {
    select: {
      id: true,
      siren: true,
      denomination: true,
      headOfficeAddress: {
        select: {
          streetNumber: true,
          streetName: true,
          postalCode: true,
          city: true,
        },
      },
    },
  },
  foreignSociety: {
    select: {
      id: true,
      denomination: true,
      registrationNumber: true,
      country: true,
    },
  },
  decennialCertificates: {
    select: {
      id: true,
      file: { select: { id: true, name: true, humanProcessingStatus: true, key: true, size: true, bucket: true, mimeType: true, fileData: { select: { type: true, issuer: true, comment: true } } } },
      activities: true,
      validityStartDate: true,
      validityEndDate: true,
      insurerSociety: {
        select: {
          id: true,
          society: {
            select: {
              id: true,
              denomination: true,
            },
          },
        },
      },
    },
  }
} satisfies Prisma.ParticipantSelect;

export const participantWithSubmissionFilesSelect = (constructionSiteSubmissionId: number) => ({
  id: true,
  designerTypes: true,
  materialTypes: true,
  society: {
    select: {
      id: true,
      siren: true,
      denomination: true,
      headOfficeAddress: {
        select: {
          streetNumber: true,
          streetName: true,
          postalCode: true,
          city: true,
        }
      }
    },
  },
  foreignSociety: {
    select: {
      id: true,
      denomination: true,
      registrationNumber: true,
      country: true,
    },
  },
  decennialCertificates: {
    select:decennialCertificateSelect
  },
  filesOnConstructionSiteSubmission: {
    select: {
      type: true,
      file: {
        select: {
          id: true,
          name: true,
          key: true,
          size: true,
          bucket: true,
          mimeType: true,
          humanProcessingStatus: true,
          fileData: {
            select: {
              type: true,
              issuer: true,
              comment: true,
            },
          },
          decennialCertificate: {
            select: {
              id: true,
              insurerSociety:{
                select: {
                  society: {
                    select: {
                      denomination: true,
                    }
                  }
                }
              }
            }
          }
        }
      },
      quoteOrFeeAgreementDetails: {
        select: {
          id: true,
        },
      },
      reportDetails: {
        select: {
          id: true,
          type: true,
        },
      },
    },
    where: {
      constructionSiteSubmissionId
    }
  },
  filesOnConstructionSiteReception: {
    select: {
      type: true,
      File: {
        select: {
          id: true,
          name: true,
          key: true,
          size: true,
          bucket: true,
          mimeType: true,
          humanProcessingStatus: true,
          fileData: {
            select: {
              type: true,
              issuer: true,
              comment: true,
            },
          },
        }
      },
    },
    where: {
      ConstructionSite: {
        constructionSiteSubmission: {
          id: constructionSiteSubmissionId
        }
      }
    }
  },
  insurers: {
    select : {
      insurerSociety : {
        select: {
          id: true,
          society: {
            select: {
              denomination: true,
            }
          }
        }
      }
    },
    where: {
      constructionSite: {
        constructionSiteSubmission:{
          id: constructionSiteSubmissionId
        }
      }
    }
  }
} satisfies Prisma.ParticipantSelect);

export const participantCompanyNameSelect = {
  society: {
    select: {
      siren: true,
      denomination: true,
    }
  },
  foreignSociety: {
    select: {
      denomination: true,
      registrationNumber: true,
    }
  }
} satisfies Prisma.ParticipantSelect;

export const decennialCertificateSelect = {
  id: true,
  file: {
    select: {
      id: true,
      name: true,
      humanProcessingStatus: true,
      key: true,
      bucket: true,
      mimeType: true,
      size: true,
      fileData: {
        select: {
          type: true,
          issuer: true,
          comment: true,
        },
      },
    },
  },
  validityEndDate: true,
  validityStartDate: true,
  activities: true,
  insurerSociety: {
    select: {
      id: true,
      society: {
        select: {
          id: true,
          denomination: true,
        },
      },
    },
  },
} satisfies Prisma.DecennialCertificateSelect;

export const contractorByIdSelector = {
  id: true,
  type: true,
  designerTypes: true,
  materialTypes: true,
  society: {
    select: {
      id: true,
      siren: true,
      denomination: true,
      headOfficeAddress: {
        select: {
          streetNumber: true,
          streetName: true,
          postalCode: true,
          city: true,
        },
      },
    },
  },
  foreignSociety: {
    select: {
      id: true,
      denomination: true,
      registrationNumber: true,
      country: true,
    },
  },
  decennialCertificates: {
    select: {
      id: true,
      file: { 
        select: { 
          id: true, 
          name: true, 
          humanProcessingStatus: true,
          key: true,
          bucket: true,
          mimeType: true,
        } 
      },
      activities: true,
      validityStartDate: true,
      validityEndDate: true,
      createdAt: true,
      updatedAt: true,
      insurerSociety: {
        select: {
          id: true,
          society: {
            select: {
              id: true,
              denomination: true,
              siren: true,
            },
          },
        },
      },
    },
    orderBy: {
      createdAt: "desc" as const,
    },
  },
  participantToConstructionSiteSubmission: {
    select: {
      id: true,
      designerType: true,
      totalMissionsOrActivitiesOrMaterialTypes: true,
      constructionSiteSubmission: {
        select: {
          id: true,
          constructionSite: {
            select: {
              id: true,
              status: true,
              createdAt: true,
              updatedAt: true,
              isSkipped: true,
              constructionSiteClientDetails: {
                select: {
                  clientSociety: {
                    select: {
                      id: true,
                      denomination: true,
                      
                    },
                  },
                },
              },
              constructionSiteDetails: {
                select: {
                  cityHallStartDate: true,
                  totalAmountTTC: true,
                  totalAmountHT: true,
                  address: {
                    select: {
                      streetNumber: true,
                      streetName: true,
                      postalCode: true,
                      city: true,
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
    orderBy: {
      createdAt: "desc" as const,
    },
  },
} satisfies Prisma.ParticipantSelect;

export const participantAbilityControlSelect = {
  id: true,
  type: true,
  societyId: true,
  society: {
    select: {
      id: true,
      brokerDetails: {
        select: {
          id: true,
        },
      },
    },
  },
} satisfies Prisma.ParticipantSelect;

export const updatedParticipantSelector = {
  id: true,
  type: true,
  society: {
    select: {
      id: true,
      siren: true,
      denomination: true,
      headOfficeAddress: {
        select: {
          streetNumber: true,
          streetName: true,
          postalCode: true,
          city: true,
        }
      },
    },
  },
  foreignSociety: {
    select: {
      id: true,
      denomination: true,
      registrationNumber: true,
      country: true,
    },
  },
} satisfies Prisma.ParticipantSelect;
